import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Drawer from '../../../components/v2/Drawer.jsx';

const MAC_PATTERN = /^([0-9A-Fa-f]{2}[:\-]){5}([0-9A-Fa-f]{2})$|^[0-9A-Fa-f]{12}$/;

/**
 * AddSpeakerDeviceDrawer — slide-out drawer for adding a SIP / Grandstream
 * speaker device.
 *
 * Form: Device Type (required), Model (required), Description (optional),
 * Serial Number with MAC-format validation (required, mirrors ServerDrawer).
 *
 * Lookup data (deviceTypes, models) is loaded once at the page level since
 * it rarely changes — passed in as props.
 *
 * @param {{
 *   isOpen: boolean,
 *   onClose: () => void,
 *   onSave: (formData: object) => Promise<void>,
 *   existingDevices: Array,
 *   campusId: number,
 *   deviceTypes: Array,
 *   models: Array,
 * }} props
 */
const AddSpeakerDeviceDrawer = ({
  isOpen,
  onClose,
  onSave,
  existingDevices,
  campusId,
  deviceTypes = [],
  models = [],
}) => {
  const drawerRef = useRef(null);

  const [deviceTypeId, setDeviceTypeId] = useState('');
  const [modelId, setModelId] = useState('');
  const [description, setDescription] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [sipExtension, setSipExtension] = useState('');
  const [siteId, setSiteId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setDeviceTypeId('');
      setModelId('');
      setDescription('');
      setSerialNumber('');
      setSipExtension('');
      setSiteId('');
      setSaving(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        const firstInput = drawerRef.current?.querySelector('select, input');
        firstInput?.focus();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const selectedType = useMemo(() => {
    if (!deviceTypeId) return null;
    return deviceTypes.find(
      (t) => String(t.device_type_id) === String(deviceTypeId)
    ) || null;
  }, [deviceTypes, deviceTypeId]);

  const selectedModel = useMemo(() => {
    if (!modelId) return null;
    return models.find(
      (m) => String(m.sip_device_id) === String(modelId)
    ) || null;
  }, [models, modelId]);

  const sortedTypes = useMemo(() =>
    [...deviceTypes].sort((a, b) =>
      (a.device_type_name || '').localeCompare(b.device_type_name || '', undefined, { sensitivity: 'base' })
    ),
  [deviceTypes]);

  const sortedModels = useMemo(() => {
    const filtered = deviceTypeId
      ? models.filter((m) => String(m.device_type_id) === String(deviceTypeId))
      : [];
    return [...filtered].sort((a, b) =>
      (a.model_name || '').localeCompare(b.model_name || '', undefined, { sensitivity: 'base' })
    );
  }, [models, deviceTypeId]);

  useEffect(() => {
    if (modelId && !sortedModels.some((m) => String(m.sip_device_id) === String(modelId))) {
      setModelId('');
      return;
    }
    if (!modelId && sortedModels.length === 1) {
      setModelId(String(sortedModels[0].sip_device_id));
    }
  }, [sortedModels, modelId]);

  const cleanedSerial = serialNumber.trim().replace(/[:\-]/g, '').toUpperCase();
  const isSerialValid = MAC_PATTERN.test(serialNumber.trim());

  const isServerType = Number(selectedType?.device_category_id) === 14;

  // SIP extension is optional, but when provided it must be a number in the
  // inclusive range 2000–2999.
  const trimmedExt = sipExtension.trim();
  const isExtensionValid =
    trimmedExt === '' ||
    (/^\d+$/.test(trimmedExt) && Number(trimmedExt) >= 2000 && Number(trimmedExt) <= 2999);

  const trimmedSiteId = siteId.trim();
  const isSiteIdValid = trimmedSiteId === '' || /^\d+$/.test(trimmedSiteId);

  const duplicateDevice = useMemo(() => {
    if (!cleanedSerial) return null;
    return (existingDevices || []).find(
      (d) => String(d.manufacture_uuid || '').replace(/[:\-]/g, '').toUpperCase() === cleanedSerial
    ) || null;
  }, [cleanedSerial, existingDevices]);

  const isFormValid =
    Boolean(deviceTypeId) &&
    Boolean(modelId) &&
    isSerialValid &&
    (isServerType ? isSiteIdValid : isExtensionValid) &&
    !duplicateDevice &&
    !saving;

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (!isFormValid) return;
      setSaving(true);

      const formData = {
        campus_id: campusId,
        manufacture_id: 10,
        manufacture_uuid: cleanedSerial,
        device_name: selectedType?.device_type_name || '',
        device_use_id: 0,
        is_active: 0,
        is_alerting_now: false,
        is_server: false,
        is_area_device: 0,
        is_resident: false,
        area_id: 0,
        resident_id: 0,
        manufacture_model_id: parseInt(modelId, 10),
        escalation_enabled: true,
        descriptions: description.trim(),
        device_type_id: parseInt(deviceTypeId, 10),
        device_category_id: selectedType?.device_category_id ?? null,
        sip_extension: isServerType || trimmedExt === '' ? null : parseInt(trimmedExt, 10),
        site_id: isServerType && trimmedSiteId !== '' ? parseInt(trimmedSiteId, 10) : null,
      };

      onSave(formData).finally(() => setSaving(false));
    },
    [isFormValid, campusId, cleanedSerial, selectedType, modelId, deviceTypeId, description, trimmedExt, trimmedSiteId, isServerType, onSave]
  );

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      panelRef={drawerRef}
      ariaLabel="Add Speaker Device"
      title="Add Speaker Device"
      rawBody
    >
      <form onSubmit={handleSubmit} className="dp-drawer__body">
        {/* Device Type */}
        <div className="dp-drawer__field">
          <label htmlFor="sp-type" className="dp-drawer__label">
            Device Type <span className="dp-drawer__required">*</span>
          </label>
          <select
            id="sp-type"
            value={deviceTypeId}
            onChange={(e) => setDeviceTypeId(e.target.value)}
            className="dp-drawer__select"
            required
          >
            <option value="">Select device type...</option>
            {sortedTypes.map((t) => (
              <option key={t.device_type_id} value={t.device_type_id}>
                {t.device_type_name}
              </option>
            ))}
          </select>
        </div>

        {/* Model */}
        <div className="dp-drawer__field">
          <label htmlFor="sp-model" className="dp-drawer__label">
            Model <span className="dp-drawer__required">*</span>
          </label>
          <select
            id="sp-model"
            value={modelId}
            onChange={(e) => setModelId(e.target.value)}
            className="dp-drawer__select"
            disabled={!deviceTypeId}
            required
          >
            <option value="">
              {!deviceTypeId
                ? 'Select a device type first...'
                : sortedModels.length === 0
                  ? 'No models available for this type'
                  : 'Select model...'}
            </option>
            {sortedModels.map((m) => (
              <option key={m.sip_device_id} value={m.sip_device_id}>
                {m.model_name}
              </option>
            ))}
          </select>
          {selectedModel?.description ? (
            <p className="dp-drawer__hint">{selectedModel.description}</p>
          ) : null}
        </div>

        {/* Description (optional) */}
        <div className="dp-drawer__field">
          <label htmlFor="sp-description" className="dp-drawer__label">
            Description
          </label>
          <input
            id="sp-description"
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description"
            className="dp-drawer__input"
          />
        </div>

        {/* SIP Extension or Site ID (server) */}
        {isServerType ? (
          <div className="dp-drawer__field">
            <label htmlFor="sp-site-id" className="dp-drawer__label">
              Site ID
            </label>
            <input
              id="sp-site-id"
              type="text"
              inputMode="numeric"
              value={siteId}
              onChange={(e) => setSiteId(e.target.value)}
              placeholder="Optional site id"
              className={`dp-drawer__input ${siteId && !isSiteIdValid ? 'is-invalid' : ''}`}
            />
            {siteId && !isSiteIdValid ? (
              <p className="dp-drawer__error">Site ID must contain digits only</p>
            ) : null}
          </div>
        ) : (
          <div className="dp-drawer__field">
            <label htmlFor="sp-extension" className="dp-drawer__label">
              SIP Extension
            </label>
            <input
              id="sp-extension"
              type="text"
              inputMode="numeric"
              value={sipExtension}
              onChange={(e) => setSipExtension(e.target.value)}
              placeholder="Optional extension (2000–2999)"
              className={`dp-drawer__input ${sipExtension && !isExtensionValid ? 'is-invalid' : ''}`}
            />
            {sipExtension && !isExtensionValid ? (
              <p className="dp-drawer__error">Extension must be a number between 2000 and 2999</p>
            ) : null}
          </div>
        )}

        {/* Serial Number (MAC format) */}
        <div className="dp-drawer__field">
          <label htmlFor="sp-serial" className="dp-drawer__label">
            Serial Number <span className="dp-drawer__required">*</span>
          </label>
          <input
            id="sp-serial"
            type="text"
            value={serialNumber}
            onChange={(e) => setSerialNumber(e.target.value)}
            placeholder="AA:BB:CC:DD:EE:FF or AABBCCDDEEFF"
            className={`dp-drawer__input ${(serialNumber && !isSerialValid) || duplicateDevice ? 'is-invalid' : ''}`}
            required
          />
          {serialNumber && !isSerialValid ? (
            <p className="dp-drawer__error">Enter a valid MAC address (e.g. AA:BB:CC:DD:EE:FF)</p>
          ) : null}
          {duplicateDevice ? (
            <p className="dp-drawer__error">A device with this serial number already exists in the system</p>
          ) : null}
        </div>

        <div className="dp-drawer__footer">
          <button
            type="button"
            onClick={onClose}
            className="dp-btn dp-btn--secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isFormValid || saving}
            className="dp-btn dp-btn--primary"
          >
            {saving ? 'Adding...' : 'Add Device'}
          </button>
        </div>
      </form>
    </Drawer>
  );
};

export default AddSpeakerDeviceDrawer;
